import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtCurrency, fmtDate, fmtDateTime, STATUS_COLOR, PRIORITY_COLOR, daysUntil } from "@/lib/utils-domain";
import { Scales, ArrowUpRight, Warning, Briefcase, CurrencyDollar, UsersThree, Clock } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const CHART_DATA = [
    { day: "Mon", hrs: 4.2 },
    { day: "Tue", hrs: 6.8 },
    { day: "Wed", hrs: 5.4 },
    { day: "Thu", hrs: 7.1 },
    { day: "Fri", hrs: 3.9 },
    { day: "Sat", hrs: 1.2 },
    { day: "Sun", hrs: 0.5 },
];

function StatCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="card-sharp p-5" data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</div>
                <Icon size={18} weight="duotone" className="text-[#0B2447]" />
            </div>
            <div className="font-display text-3xl font-extrabold text-slate-900 mt-3 tracking-tight">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data));
    }, []);

    if (!stats) return <div className="text-sm text-slate-500 font-mono">Loading…</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Dashboard</div>
                    <h1 className="font-display text-4xl font-extrabold text-slate-900 tracking-tight mt-1">
                        Today at the firm.
                    </h1>
                </div>
                <Link to="/app/cases" className="btn-primary inline-flex items-center gap-2" data-testid="open-case-button">
                    Open a case <ArrowUpRight size={14} weight="bold" />
                </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Briefcase} label="Open cases" value={stats.cases_open} sub={`${stats.cases_total} total`} testid="stat-cases-open" />
                <StatCard icon={UsersThree} label="Active leads" value={stats.leads_active} sub="In pipeline" testid="stat-leads" />
                <StatCard icon={Clock} label="Billable hours · MTD" value={(stats.billable_minutes_month / 60).toFixed(1)} sub="This month" testid="stat-hours" />
                <StatCard icon={CurrencyDollar} label="Billed · MTD" value={fmtCurrency(stats.billed_amount_month)} sub="Logged time" testid="stat-billed" />
            </div>

            {/* Main grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Critical Deadlines */}
                <div className="lg:col-span-2 card-sharp p-6" data-testid="critical-deadlines-panel">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-600">Critical legal deadlines</div>
                            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Next 7 days</h2>
                        </div>
                        <Link to="/app/tasks" className="text-xs text-[#0B2447] hover:underline font-semibold">View all →</Link>
                    </div>
                    {stats.critical_tasks?.length === 0 ? (
                        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-sm">
                            No urgent deadlines. You're clear.
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {stats.critical_tasks.map((t) => {
                                const days = daysUntil(t.due_date);
                                const urgent = days !== null && days <= 2;
                                return (
                                    <li key={t.task_id} className="py-3 flex items-center gap-4">
                                        <div className={`w-1 self-stretch ${urgent ? "bg-red-500" : "bg-amber-400"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">Due {fmtDate(t.due_date)} · {days} day{days === 1 ? "" : "s"}</div>
                                        </div>
                                        <span className={`badge ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Hours chart */}
                <div className="card-sharp p-6" data-testid="hours-chart-panel">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Billable trend</div>
                    <h2 className="font-display text-xl font-bold text-slate-900 mt-1 mb-4">This week</h2>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={CHART_DATA}>
                            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                            <YAxis hide />
                            <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                            <Bar dataKey="hrs" fill="#0B2447" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Recent cases */}
                <div className="lg:col-span-2 card-sharp p-6" data-testid="recent-cases-panel">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Active cases</div>
                            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Recent activity</h2>
                        </div>
                        <Link to="/app/cases" className="text-xs text-[#0B2447] hover:underline font-semibold">View all →</Link>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {stats.recent_cases.map((c) => (
                            <Link
                                key={c.case_id}
                                to={`/app/cases/${c.case_id}`}
                                data-testid={`case-row-${c.case_id}`}
                                className="flex items-center justify-between py-3 group hover:bg-slate-50/50 -mx-2 px-2 transition"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <Scales size={18} weight="duotone" className="text-[#0B2447] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="font-semibold text-sm truncate group-hover:text-[#0B2447]">{c.name}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">{c.client_name} · {c.case_type}</div>
                                    </div>
                                </div>
                                <span className={`badge ${STATUS_COLOR[c.status]}`}>{c.status}</span>
                            </Link>
                        ))}
                        {stats.recent_cases.length === 0 && (
                            <div className="text-sm text-slate-500 py-6 text-center">No cases yet. Open your first one.</div>
                        )}
                    </div>
                </div>

                {/* Activity feed */}
                <div className="card-sharp p-6" data-testid="activity-feed-panel">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Activity</div>
                    <h2 className="font-display text-xl font-bold text-slate-900 mt-1 mb-4">Latest</h2>
                    <ul className="space-y-3 text-sm">
                        {stats.activities.slice(0, 8).map((a) => (
                            <li key={a.activity_id} className="flex gap-3">
                                <Warning size={14} weight="duotone" className="text-slate-400 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-slate-700 truncate">{a.detail}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">{fmtDateTime(a.timestamp)}</div>
                                </div>
                            </li>
                        ))}
                        {stats.activities.length === 0 && (
                            <li className="text-slate-500 text-sm">Activity will appear here.</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}
